import { clsx } from "clsx";
import camelCase from "lodash/camelCase";

import stiqTwMerge from "./stiq-tw-merge";

export function cn(...inputs) {
  return stiqTwMerge(clsx(inputs));
}

export function objToCamelCase(obj) {
  // Handle null or undefined
  if (obj == null) {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((val) => objToCamelCase(val));
  }

  // Handle objects
  if (typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = camelCase(key);
      acc[camelKey] = objToCamelCase(obj[key]); // Recursively process nested values
      return acc;
    }, {});
  }

  // Return primitive values as-is
  return obj;
}

/**
 * Calculate the difference between two values
 * @param {number} value - The value to calculate the difference from
 * @param {number} total - The total value
 * @param {boolean} reverse - Whether to reverse the calculation
 * @returns {number} The difference between the two values
 */
export function calculateDifference(value, total, reverse = false) {
  if (!total) return 0;
  return reverse ? total - value : value - total;
}

/**
 * Calculate the percentage difference between two values
 * @param {number} value - The value to calculate the percentage from
 * @param {number} total - The total value
 * @param {boolean} reverse - Whether to reverse the calculation
 * @returns {number} The percentage difference between the two values
 */
export function calculatePercentage(value, total, reverse = false) {
  const difference = calculateDifference(value, total, reverse);
  if (difference === 0) return 0;

  return Math.round((difference / total) * 100);
}

/**
 * Format a number as currency in the specified locale
 * @param {number} amount - The amount to format
 * @param {string} locale - The locale to use for formatting (defaults to 'en-IN')
 * @param {string} currency - The currency code to use (defaults to 'INR')
 * @returns {string} The formatted currency string
 */
export function formatCurrency(amount, locale = "en-IN", currency = "INR") {
  if (!amount && amount !== 0) return "";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  })
    .format(amount)
    .replace(/\.\d+$/, "");
}

/**
 * Get the device type based on screen width
 * @returns {string} Device type (desktop, tablet, or mobile)
 */
export function getDeviceType() {
  if (typeof window === "undefined") return "unknown";

  const width = window.innerWidth;
  if (width >= 1024) return "desktop";
  if (width >= 768) return "tablet";
  return "mobile";
}

/**
 * Get OS details from user agent
 * @returns {string} Operating system name
 */
export function getOSDetails() {
  if (typeof window === "undefined") return "unknown";

  const userAgent = window.navigator.userAgent;

  if (/Windows/i.test(userAgent)) return "Windows";
  if (/Macintosh|Mac OS X/i.test(userAgent)) return "MacOS";
  if (/Linux/i.test(userAgent)) return "Linux";
  if (/Android/i.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iOS";

  return "Other";
}

export function getCourseType(courseTypeId) {
  let courseType = "COURSE";

  switch (Number.parseInt(courseTypeId, 10)) {
    case 2:
    case 5:
      courseType = "BOOK";
      break;
    default:
      courseType = "COURSE";
  }

  return courseType;
}

export function getCourseCategoryType(categoryId) {
  if (categoryId === 1) {
    return "Smart";
  }
  if (categoryId === 2) {
    return "Micro";
  }
  if (categoryId === 3) {
    return "Quiz Book";
  }
  if (categoryId === 4) {
    return "eBooks";
  }
  if (categoryId === 5) {
    return "Books";
  }
  if (categoryId === 6) {
    return "Testseries";
  }
  if (categoryId === 7) {
    return "Live Classes";
  }
  return "Course category";
}

export function getFolderResources(folder) {
  const setResourceType = (type) => (resource) => ({
    ...resource,
    type,
  });

  return [].concat(
    (folder.videos || []).map(setResourceType("VIDEO")),
    (folder.text || []).map(setResourceType("TEXT")),
    (folder.quiz || []).map(setResourceType("QUIZ")),
    (folder.testseriesData || []).map(setResourceType("TEST"))
  );
}

export function structureCourseFolder(folders, folderIndex = null) {
  return folders.map((folder, index) => {
    const folderStrucureIndex = folderIndex
      ? `${folderIndex}.${index + 1}`
      : index + 1;

    if (folder?.subFolder) {
      return {
        ID: folder.id,
        name: folder.folderName,
        slug: folder.folderSlug,
        type: "FOLDER",
        contentType: folder.elementContentType,
        folderIndex: folderStrucureIndex,
        folder: structureCourseFolder(folder.subFolder, folderStrucureIndex),
      };
    }

    return {
      ID: folder.id,
      name: folder.folderName || folder.subFolderName,
      slug: folder.folderSlug || folder.subFolderSlug,
      type: "FOLDER",
      contentType: folder.elementContentType,
      folderIndex: folderStrucureIndex,
      resources: getFolderResources(folder),
    };
  });
}

export function languageMapping(languageID) {
  switch (languageID) {
    case 1:
      return "en";
    case 2:
      return "hn"; // en ?
    case 3:
      return "od";
    case 4:
      return "mh";
    case 5:
      return "kan"; // kn ?
    case 6:
      return "gj";
    case 7:
      return "tm";
    case 8:
      return "hi";
    case 9:
      return "en"; // ml->en
    case 10:
      return "bg";
    default:
      return null;
  }
}

export function addCourseToLocalStorage(courseDetail, storageKey) {
  let courses = window.localStorage.getItem(storageKey) || "[]";
  try {
    courses = JSON.parse(courses);
  } catch {
    courses = [];
  }

  const courseIndex = courses?.findIndex(
    (course) =>
      Number.parseInt(course.courseId, 10) ===
      Number.parseInt(courseDetail.courseId, 10)
  );
  if (courseIndex > -1 && Array.isArray(courses)) {
    // remove the current product from its index
    courses.splice(courseIndex, 1);
  }

  // push current product
  courses.push(courseDetail);

  // remove last entry after 10 records
  if (courses.length > 10) {
    courses.shift();
  }

  window.localStorage.setItem(storageKey, JSON.stringify(courses));
}

export function removeCourseFromLocalStorage(courseId, storageKey) {
  let courses = window.localStorage.getItem(storageKey) || "[]";
  try {
    courses = JSON.parse(courses);
  } catch {
    courses = [];
  }

  const courseIndex = courses?.findIndex(
    (course) => course.courseId === courseId
  );
  if (courseIndex > -1 && Array.isArray(courses)) {
    // remove the current product from its index
    courses.splice(courseIndex, 1);
  }

  // remove last entry after 10 records
  if (courses.length > 10) {
    courses.shift();
  }

  window.localStorage.setItem(storageKey, JSON.stringify(courses));
}

export function getContinuePurchaseCourses() {
  let courses = window.localStorage.getItem(
    process.env.NEXT_PUBLIC_CONTINUE_PURCHASE_STORAGE_KEY
  );

  try {
    courses = JSON.parse(courses) || [];
  } catch {
    courses = [];
  }

  return courses;
}

export function addContinuePurchaseCourse(courseDetail) {
  if (!courseDetail) return;

  addCourseToLocalStorage(
    courseDetail,
    process.env.NEXT_PUBLIC_CONTINUE_PURCHASE_STORAGE_KEY
  );
}

export function removeContinuePurchaseCourse(courseId) {
  if (!courseId) return;

  removeCourseFromLocalStorage(
    courseId,
    process.env.NEXT_PUBLIC_CONTINUE_PURCHASE_STORAGE_KEY
  );
}

export function addRecentlyViewedCourse(courseDetail) {
  if (!courseDetail) return;

  addCourseToLocalStorage(
    courseDetail,
    process.env.NEXT_PUBLIC_RECENT_VISITED_STORAGE_KEY
  );
}

export function removeRecentlyViewedCourse(courseId) {
  if (!courseId) return;

  removeCourseFromLocalStorage(
    courseId,
    process.env.NEXT_PUBLIC_RECENT_VISITED_STORAGE_KEY
  );
}

export function getRecentlyViewedCourses() {
  let courses = window.localStorage.getItem(
    process.env.NEXT_PUBLIC_RECENT_VISITED_STORAGE_KEY
  );

  try {
    courses = JSON.parse(courses) || [];
  } catch {
    courses = [];
  }

  return courses;
}

export function convertToSlug(text) {
  return (text || "")
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "");
}

export function getYouTubeChannelTier(subscriberCount) {
  if (subscriberCount >= 100000000) {
    return "RED_DIAMOND";
  } else if (subscriberCount >= 10000000) {
    return "DIAMOND";
  } else if (subscriberCount >= 1000000) {
    return "GOLD";
  } else if (subscriberCount >= 100000) {
    return "SILVER";
  }

  return "NO_AWARD";
}

/**
 * Ensures `categoryList` is present in the section order array.
 * If already present, returns the order as-is.
 * If absent, inserts it right after `topBanner`, or at the start if `topBanner` isn't in the order.
 * @param {string[]} order - The section order array from the API
 * @returns {string[]} The resolved order with `categoryList` guaranteed
 */
export function resolveStoreOrder(order) {
  if (order.includes("categoryList")) return order;

  if (order.includes("topBanner")) {
    return order.reduce((acc, item) => {
      acc.push(item);
      if (item === "topBanner") acc.push("categoryList");
      return acc;
    }, []);
  }

  return ["categoryList", ...order];
}
